import 'primeicons/primeicons.css';
import './RTUMap.css';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import GoogleMapReact from 'google-map-react';
import Loader from '../../common/Loader';
import MarkerClusterer from '@google/markerclustererplus';
import { RTU } from '../../../types/RTU';
import _ from 'lodash';
import { getStatusColor } from '../../../utils/statusConfig';

const defaultCenter = { lat: 31.6225, lng:  -7.9898};

// Item 7: legend entries mirror the canonical statuses used to color markers.
// Rebooting shares the same color as Offline in getStatusColor, so it is
// intentionally omitted to avoid a misleading duplicate swatch.
const MAP_LEGEND_ITEMS = [
  { label: 'Online', status: 'online' },
  { label: 'Unstable', status: 'unstable' },
  { label: 'Offline / Not Reachable', status: 'offline' },
];


const defaultZoom = 0;

interface RTUMapProps {
  rtus: RTU[];
  currentRTU?: RTU;
  setRtuSearchResult: (rtus: RTU[]) => void;
  setSearchQuery: (query: string) => void;
  hoveredRTU: RTU | null;
  findRTUsInBounds: (ne: any, se: any) => void;
}

const RTUMap: React.FC<RTUMapProps> = ({ rtus, currentRTU, setSearchQuery, setRtuSearchResult, hoveredRTU, findRTUsInBounds }) => {

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const mapRef = useRef<any>(null);
  // Remembers the last rtus array rendered, so a marker rebuild triggered only by
  // a selection change does not re-fit the map bounds (which would fight the
  // pan/zoom-to-selected effect below).
  const lastRenderedRtusRef = useRef<RTU[] | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isApiLoaded, setIsApiLoaded] = useState(false); // New state to track API load
  const [loadingMap, setLoadingMap] = useState(true);


  // const [isDataLoaded, setIsDataLoaded] = useState(false);

  const handleApiLoaded = (map: any, maps: any) => {
    setIsApiLoaded(true); // Set API loaded state to true
    mapRef.current = map; // Store the Google Maps instance
    updateMarkersWithRetry(map, maps);

    if (searchInputRef.current && maps.places) {
      const autocomplete = new maps.places.Autocomplete(searchInputRef.current);
      autocomplete.bindTo('bounds', map);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) {
          return;
        }
        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport);
        } else {
          map.panTo(place.geometry.location);
          map.setZoom(14);
        }
      });
    }
};

  const clearMarkers = () => {
    if (!isApiLoaded) return; // Guard against API not loaded
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Clear existing clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }
  };


  // const createClusterIcon = (markers, numStyles) => {
  //   const totalRTUs = markers.length;
  //   const onlineRTUs = markers.filter(marker => {
  //     const rtu = markerToRTUMap.get(marker);
  //     return rtu && rtu.status === 'online';
  //   }).length;
  
  //   // If all RTUs are online or all are offline, we want to draw a full circle for the respective color
  //   const allOnline = onlineRTUs === totalRTUs;
  //   const allOffline = onlineRTUs === 0;
  
  //   const svgSize = 50; // Size of the SVG icon
  //   const svgIcon = `
  //     <svg width="${svgSize}" height="${svgSize}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
  //       ${allOffline ? `<circle r="16" cx="16" cy="16" fill="red" />` : ''}
  //       ${allOnline ? `<circle r="16" cx="16" cy="16" fill="green" />` : ''}
  //       ${!allOnline && !allOffline ? `<circle r="16" cx="16" cy="16" fill="red" />
  //       <path d="M16,0 A16,16 0 ${onlineRTUs > totalRTUs / 2 ? '1 1' : '0 1'} 16,32 A16,16 0 ${onlineRTUs > totalRTUs / 2 ? '1 1' : '0 1'} 16,0" fill="green" />` : ''}
  //       <circle r="9" cx="16" cy="16" fill="white" />
  //       <text x="16" y="16" font-size="7" font-weight="bold" text-anchor="middle" alignment-baseline="central" fill="black">${totalRTUs}</text>
  //     </svg>
  //   `;
  
  //   // Log the SVG for debugging
  //   console.log(svgIcon);
  
  //   // Return the cluster icon info
  //   return {
  //     text: svgIcon, // Use the SVG as the marker icon
  //     index: 1,
  //     title: '',
  //   };
  // };
  
  const createClusterIcon = (markers: any, numStyles: any) => {
    const totalRTUs = markers.length;
    const onlineRTUs = markers.filter((marker: any) => {
      const rtu = markerToRTUMap.get(marker);
      return rtu && rtu.status === 'online';
    }).length;
  
    // These ratios will now be between 0 and 1
    const onlineRatio = onlineRTUs / totalRTUs;
    const offlineRatio = (totalRTUs - onlineRTUs) / totalRTUs;
  
    // If all RTUs are online or offline, adjust the angles to fill the circle
    const fullCircle = 359.999; // Use a value just under 360 to draw a full circle with a path
  
    // Calculate angles for the SVG paths
    const onlineAngle = onlineRatio === 1 ? fullCircle : onlineRatio * 360;
  
    const svgSize = 50; // Size of the SVG icon
    const svgIcon = `
      <svg width="${svgSize}" height="${svgSize}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        ${offlineRatio > 0 ? `<circle r="16" cx="16" cy="16" fill="#6c757d" />` : ''}
        ${onlineRatio === 1 ? `<circle r="16" cx="16" cy="16" fill="#28a745" />` : ''}

        ${onlineRatio > 0 ? `<path d="M16,16 L16,0 A16,16 0 ${onlineAngle > 180 ? 1 : 0},1 ${16 + 16 * Math.sin(2 * Math.PI * onlineRatio)},${16 - 16 * Math.cos(2 * Math.PI * onlineRatio)} Z" fill="#28a745"/>` : ''}
        <circle r="9" cx="16" cy="16" fill="white" />
        <text x="16" y="16" font-size="7" font-weight="bold" text-anchor="middle" alignment-baseline="central" fill="black">${totalRTUs}</text>
      </svg>
    `;
  
    // Log the SVG for debugging
    // console.log(svgIcon);
  
    // Return the cluster icon info
    return {
      text: svgIcon, // Use the SVG as the marker icon
      index: 1,
      title: '',
    };
  };
  
  
  const markerToRTUMap = new Map();

  const updateMarkers = (map: any, maps: any, fitView: boolean = true) => {
    if (!window.google || !window.google.maps || !map || !maps) {
      return;
    }

    clearMarkers();
    
    if (!rtus || rtus.length === 0) {
      return;
    }

    if (!mapRef.current) {
        return;
    }

    const bounds = new maps.LatLngBounds();
 
    // Add new markers
    const markers = rtus.map(rtu => {

      const latLng = new maps.LatLng(rtu.latitude, rtu.longitude);
      bounds.extend(latLng);
  
      const isSelected = currentRTU?.id === rtu.id;

      // Define the SVG marker icon
      const svgIcon = {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5 7 13 7 13s7-8 7-13c0-3.87-3.13-7-7-7z',
        // Bug 3154: render the full canonical palette (Unstable/Rebooting
        // included), not just an online/offline binary.
        fillColor: getStatusColor(rtu.status),
        fillOpacity: 1,
        strokeWeight: isSelected ? 3 : 1,
        strokeColor: isSelected ? '#FFB300' : 'black',
        scale: isSelected ? 2.8 : 2, // Scale for size adjustment; larger when selected
        anchor: new google.maps.Point(15, 30) // Adjust anchor to correctly position the icon
      };
      
      const marker = new maps.Marker({
        position: new google.maps.LatLng({lat:rtu.latitude, lng:rtu.longitude}),
        icon: svgIcon, // Set the custom SVG icon
        // ... other marker options
      });
      markerToRTUMap.set(marker, rtu);

      // Add event listener for mouseover (to show tooltip)
      marker.addListener('mouseover', () => {
        const content = `
        <div>
          <strong>Name:</strong> ${rtu.name}<br/>
          <strong>CPU:</strong> ${rtu.cpuUsage.toFixed(2)}<b>%</b><br/>
          <strong>Disk:</strong> ${rtu.diskUsage.toFixed(2)}<b>%</b><br/>
          <strong>Location:</strong> ${rtu.location}<br/>
          <strong>Client:</strong> ${rtu.client}
        </div>
        `;
        setTooltipContent(content);
  
        // Convert the marker's lat/lng position to a pixel position relative to the map container
        const scale = Math.pow(2, map.getZoom());
        const nw = new maps.LatLng({
          lat:map.getBounds().getNorthEast().lat(),
          lng:map.getBounds().getSouthWest().lng()
        }
        );
        const worldCoordinateNW = map.getProjection().fromLatLngToPoint(nw);
        const worldCoordinate = map.getProjection().fromLatLngToPoint(marker.getPosition());
        const pixelOffset = new maps.Point(
          Math.floor((worldCoordinate.x - worldCoordinateNW.x) * scale),
          Math.floor((worldCoordinate.y - worldCoordinateNW.y) * scale)
        );
  
        setTooltipPosition({
          left: pixelOffset.x,
          top: pixelOffset.y
        });
      });
  
      // Add event listener for mouseout (to hide tooltip)
      marker.addListener('mouseout', () => {
        setTooltipContent('');
      });

      return marker;
    });
  
    markersRef.current = markers;

    // Only re-fit the viewport when the underlying rtus set changed, not when a
    // rebuild was triggered by a selection (that is handled by the pan-to-selected
    // effect so the map does not jump back out to "fit all" on every click).
    if (fitView) {
      if (rtus.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(10); // You can adjust the zoom level
      } else {
        map.fitBounds(bounds); // Adjusts the map view to ensure all markers are visible
      }
    }
    // Reinitialize clustering
    if (markers.length > 0) {
     
      
      clustererRef.current = new MarkerClusterer(map, markers, {
        gridSize: 60,
        styles: [{
          height: 50,
          width: 50,
          textColor: '#000',
          textSize: 14,
          url: ''
          // Notice there's no URL here, as the URL will come from the calculator function
        }],
        calculator: createClusterIcon, // Your custom calculator function
      });
      
    } else {
    }
    setLoadingMap(false);
  };


  const updateMarkersWithRetry = (map: any, maps: any, retryCount = 0, fitView: boolean = true) => {
    if ((!window.google || !window.google.maps || !map || !maps) && retryCount < 3) {
      setTimeout(() => updateMarkersWithRetry(map, maps, retryCount + 1, fitView), 1000); // Retry after 1 second
      return;
    }

    updateMarkers(map, maps, fitView);
  };
  
  // Use updateMarkersWithRetry in place of updateMarkers
  


  // lodash debounce wraps the callback; exhaustive-deps cannot see the inner function.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(_.debounce((ne, sw) => {
    findRTUsInBounds(ne, sw);
  }, 200), [findRTUsInBounds]);
  
  const handleBoundsChanged = (bounds: any) => {
    debouncedUpdate(bounds.ne, bounds.sw);
};


  
 

  useEffect(() => {
    if (mapRef.current && isApiLoaded) {
      // Fit the viewport only when the rtus set itself changed; a rebuild caused
      // purely by a selection change just re-renders markers (for the highlight)
      // and lets the pan-to-selected effect own the camera.
      const fitView = lastRenderedRtusRef.current !== rtus;
      lastRenderedRtusRef.current = rtus;
      updateMarkersWithRetry(mapRef.current, window.google.maps, 0, fitView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtus, currentRTU]);

  // Item 3: pan/zoom the map to the selected RTU (driven by list selection).
  useEffect(() => {
    const map = mapRef.current;
    if (!isApiLoaded || !map || !currentRTU) {
      return;
    }
    if (!Number.isFinite(currentRTU.latitude) || !Number.isFinite(currentRTU.longitude)) {
      return;
    }
    map.panTo({ lat: currentRTU.latitude, lng: currentRTU.longitude });
    map.setZoom(12);
  }, [currentRTU, isApiLoaded]);



  return (
    <div 
    style={{height: '100%', width: '100%', position: 'relative'}}
    >
      {loadingMap && 
        <Loader message='Loading the map'/>
      }
      <input 
            ref={searchInputRef} 
            type="text" 
            placeholder="Search places..." 
            style={{ 
              position: 'absolute', 
              top: '10px', 
              left: '10px', 
              zIndex: 1000, 
              padding: '10px', 
              border: '1px solid gray', 
              borderRadius: '4px' 
            }} 
      />
      <GoogleMapReact
        ref={mapRef}
        bootstrapURLKeys={{
          key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
          libraries: ['places']
        }}
        defaultCenter={
          // rtus.length !== 0 ? 
          // calculateCenter(rtus) : 
          defaultCenter
        }
        defaultZoom={defaultZoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({map, maps}) => {
          handleApiLoaded(map, maps);
        }}      
        onChange={({ center, zoom, bounds, marginBounds }) => {
          handleBoundsChanged({
            ne: { lat: bounds.ne.lat, lng: bounds.ne.lng },
            sw: { lat: bounds.sw.lat, lng: bounds.sw.lng },
          });
        }}
      />
      <div className="map-status-legend">
        {MAP_LEGEND_ITEMS.map((item) => (
          <div className="map-status-legend-item" key={item.status}>
            <span
              className="map-status-legend-swatch"
              style={{ backgroundColor: getStatusColor(item.status) }}
            />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
      {tooltipContent && (
        <div
        className="custom-tooltip"
        style={{ 
          left: tooltipPosition.left, 
          top: tooltipPosition.top, 
          display: tooltipContent ? 'block' : 'none' 
        }} 
        dangerouslySetInnerHTML={{ __html: tooltipContent }} 
        />
      )}
    </div>
  );
}

export default RTUMap;
